import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Users, Palmtree } from "lucide-react";
import { SectionBody, SectionHead, SectionSummary } from "@/components/SectionCollapse";

const SLOTS = [
  { key: "hauptverantwortlich_user_id", label: "Hauptverantwortlich" },
  { key: "konstruktion_user_id", label: "Konstruktion" },
  { key: "produktion_user_id", label: "Produktion" },
  { key: "versand_user_id", label: "Versand" },
  { key: "buchhaltung_user_id", label: "Buchhaltung" },
];

export default function TeamSection({ orderId, collapseKey }) {
  const [users, setUsers] = useState([]);
  const [team, setTeam] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [u, t] = await Promise.all([api.get("/team/users-lite"), api.get(`/auftraege/${orderId}/team`)]);
    setUsers(u.data); setTeam(t.data);
  };
  useEffect(() => { load();  }, [orderId]);

  if (!team) return null;

  const setSlot = (key, v) => setTeam({ ...team, [key]: v || null });
  const toggleWatcher = (uid) => {
    const cur = team.watcher_user_ids || [];
    setTeam({ ...team, watcher_user_ids: cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/auftraege/${orderId}/team`, team);
      setTeam(r.data); toast.success("Team gespeichert.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  const userLabel = (u) => u.is_out_of_office ? `${u.name} 🌴` : u.name;
  const lead = users.find((u) => u.id === team.hauptverantwortlich_user_id);

  return (
    <Card className="border-slate-200" data-testid="team-section">
      <CardContent className="p-5 space-y-4">
        <SectionHead k={collapseKey} label="Team" className="flex items-center gap-2">
          <h2 className="text-base font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> Team</h2>
          <SectionSummary k={collapseKey}>{lead ? `Hauptverantwortlich: ${lead.name}` : "nicht zugewiesen"}</SectionSummary>
        </SectionHead>
        <SectionBody k={collapseKey} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SLOTS.map((s) => (
            <div key={s.key}>
              <Label>{s.label}</Label>
              <Select value={team[s.key] || "__none__"} onValueChange={(v) => setSlot(s.key, v === "__none__" ? null : v)}>
                <SelectTrigger data-testid={`team-slot-${s.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— nicht zugewiesen —</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{userLabel(u)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <div>
          <Label>Watcher</Label>
          <div className="flex flex-wrap gap-2 mt-1" data-testid="team-watchers">
            {users.map((u) => {
              const active = (team.watcher_user_ids || []).includes(u.id);
              return (
                <button key={u.id} onClick={() => toggleWatcher(u.id)} type="button"
                  className={`text-xs px-2.5 py-1 rounded-full border ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                  data-testid={`team-watcher-${u.email}`}>
                  {userLabel(u)}
                </button>
              );
            })}
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="team-save-button">Team speichern</Button>
        </SectionBody>
      </CardContent>
    </Card>
  );
}
